import { crmAuthedFetch } from './crmAuthedFetch';

const FUNCTIONS_URL =
  process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  'https://us-central1-lctf-projects.cloudfunctions.net';

/**
 * Best-effort fallback when the onSalesOrderCreated Cloud Function does not run
 * (or auto-invoice is enabled but the trigger missed). Idempotent — the HTTP
 * handler returns 409 if an invoice already exists.
 */
export type InstallmentId = 'deposit' | 'pre_shipment' | 'final';

export async function triggerQuickBooksInvoiceForSalesOrder(
  salesOrderId: string,
  installmentId?: InstallmentId,
  options?: {
    includeImmediateChangeOrders?: boolean;
    consolidateImmediateChangeOrders?: boolean;
    consolidateChangeOrderIds?: string[];
    /** Structure groups on a partial 40% or 10% invoice. Omit to bill the whole milestone. */
    structureIds?: string[];
    structureGroupKeys?: string[];
    structureGroupLabels?: string[];
    omittedPackageLabels?: string[];
    structureGroups?: Array<{ key: string; label: string; structureIds: string[] }>;
    /** Present on a partial invoice, including an empty list when no change order is folded in. */
    foldChangeOrderRefs?: string[];
  },
): Promise<{ ok: boolean; invoiceNumber?: string; error?: string }> {
  if (!salesOrderId) return { ok: false, error: 'Missing sales order id' };
  try {
    const body: Record<string, unknown> = { salesOrderId };
    if (installmentId) body.installmentId = installmentId;
    if (options?.structureIds?.length) {
      body.structureIds = options.structureIds;
      if (options.structureGroupKeys?.length) body.structureGroupKeys = options.structureGroupKeys;
      if (options.structureGroupLabels?.length) body.structureGroupLabels = options.structureGroupLabels;
      if (options.omittedPackageLabels?.length) body.omittedPackageLabels = options.omittedPackageLabels;
      if (options.structureGroups?.length) body.structureGroups = options.structureGroups;
    }
    const foldChangeOrderRefs = options?.foldChangeOrderRefs;
    if (Array.isArray(foldChangeOrderRefs)) {
      body.foldChangeOrderRefs = foldChangeOrderRefs;
    }
    if (options?.consolidateImmediateChangeOrders) {
      body.consolidateImmediateChangeOrders = true;
      if (options.consolidateChangeOrderIds?.length) {
        body.consolidateChangeOrderIds = options.consolidateChangeOrderIds;
      }
    } else if (options?.includeImmediateChangeOrders) {
      body.includeImmediateChangeOrders = true;
    }
    const resp = await crmAuthedFetch(`${FUNCTIONS_URL}/createQuickBooksInvoice`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await resp.json().catch(() => ({}));
    if (resp.ok && data.success) {
      return { ok: true, invoiceNumber: data.invoiceNumber };
    }
    if (resp.status === 409 && data.quickbooksInvoiceId) {
      return { ok: true, invoiceNumber: data.invoiceNumber };
    }
    return { ok: false, error: data.error || `HTTP ${resp.status}` };
  } catch (err) {
    console.warn('[QB] triggerQuickBooksInvoiceForSalesOrder failed', err);
    return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}
